// Lightweight lot-location dialog. Purely informational metadata; never
// affects qty_available. Reachable from the "Lokasyon" column button on
// the existing Inventory → Lots table.
import React from "react";
import { api } from "@/lib/api";
import { Btn, Modal, Input, Select } from "@/components/UI";
import { Plus, Trash2, ArrowRightLeft, Save, RotateCcw } from "lucide-react";

export default function LotLocationsDialog({ lot, onClose, onSaved }) {
  const [state, setState] = React.useState(null);
  const [locations, setLocations] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [moveOpen, setMoveOpen] = React.useState(false);
  const [move, setMove] = React.useState({ from_code: "", to_code: "", qty: 0 });

  const load = React.useCallback(async () => {
    try {
      const [locs, dist] = await Promise.all([
        api.get("/locations"),
        api.get(`/lots/${lot.id}/locations`),
      ]);
      setLocations(locs || []);
      setState(dist);
    } catch (e) { alert("Yüklenemedi: " + (e?.response?.data?.detail || e.message)); }
  }, [lot?.id]);
  React.useEffect(() => { if (lot?.id) load(); }, [lot?.id, load]);

  if (!state) return null;
  const unassigned = state.unassigned;

  const setEntryCode = (i, v) => setState(s => ({
    ...s, entries: s.entries.map((e, j) => j === i ? { ...e, location_code: v.toUpperCase() } : e),
  }));
  const setEntryQty = (i, v) => setState(s => ({
    ...s, entries: s.entries.map((e, j) => j === i ? { ...e, qty: Number(v) || 0 } : e),
  }));
  const addRow = () => setState(s => ({ ...s, entries: [...s.entries, { location_code: "", qty: 0 }] }));
  const removeRow = (i) => setState(s => ({ ...s, entries: s.entries.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const entries = state.entries
        .filter(e => (e.location_code || "").trim() && Number(e.qty) > 0)
        .map(e => ({ location_code: e.location_code, qty: Number(e.qty) }));
      const r = await api.put(`/lots/${lot.id}/locations`, { entries });
      onSaved?.(r);
      await load();
    } catch (e) { alert("Kaydedilemedi: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const doMove = async () => {
    if (!move.from_code || !move.to_code || move.qty <= 0) return;
    setBusy(true);
    try {
      await api.post(`/lots/${lot.id}/locations/move`, move);
      setMoveOpen(false);
      setMove({ from_code: "", to_code: "", qty: 0 });
      await load();
      onSaved?.();
    } catch (e) { alert("Taşıma başarısız: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const assignedTotal = state.entries.reduce((a, b) => a + Number(b.qty || 0), 0);
  const over = assignedTotal > state.qty_available + 1e-6;

  return (
    <Modal open={true} onClose={onClose} title={`Lot Lokasyonları — ${state.lot_code || lot.lot_code || ""}`} size="md">
      <div className="grid grid-cols-3 gap-2 text-sm mb-3" data-testid="lot-locations-summary">
        <Stat label="Toplam Lot Miktarı" value={state.qty_available} tone="slate"/>
        <Stat label="Atanan" value={assignedTotal} tone={over ? "rose" : "emerald"}/>
        <Stat label="Atanmayan" value={Math.max(0, unassigned)} tone={unassigned > 0 ? "amber" : "slate"}/>
      </div>
      {over && <div className="text-xs text-rose-600 mb-2">⚠ Atanan miktar toplam lot miktarını aşıyor. Bilgi amaçlıdır, kayıt yine de yapılabilir.</div>}

      <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1" data-testid="lot-locations-list">
        {state.entries.length === 0 && (
          <div className="text-xs text-slate-500 italic p-2 border border-dashed border-slate-300 rounded">Henüz lokasyon atanmamış. İsteğe bağlıdır.</div>
        )}
        {state.entries.map((e, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`lot-loc-row-${i}`}>
            <input
              list={`loc-codes-${lot.id}`}
              value={e.location_code}
              onChange={(ev) => setEntryCode(i, ev.target.value)}
              placeholder="Lokasyon kodu (örn: HMD-A-01-03)"
              className="flex-1 text-sm px-2 py-1 border border-slate-200 rounded font-mono uppercase"
              data-testid={`lot-loc-code-${i}`}
              maxLength={40}
            />
            <input
              type="number"
              step="0.001"
              value={e.qty}
              onChange={(ev) => setEntryQty(i, ev.target.value)}
              placeholder="Miktar"
              className="w-32 text-sm px-2 py-1 border border-slate-200 rounded"
              data-testid={`lot-loc-qty-${i}`}
              min={0}
            />
            <button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded" data-testid={`lot-loc-remove-${i}`}>
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
        <datalist id={`loc-codes-${lot.id}`}>
          {locations.map(l => <option key={l.id} value={l.code}>{l.description || l.warehouse || ""}</option>)}
        </datalist>
      </div>

      <div className="flex gap-2 mt-3 flex-wrap">
        <Btn size="sm" variant="secondary" onClick={addRow} data-testid="lot-loc-add"><Plus size={12}/> Satır Ekle</Btn>
        {state.entries.length >= 2 && (
          <Btn size="sm" variant="secondary" onClick={() => setMoveOpen(true)} data-testid="lot-loc-move-open"><ArrowRightLeft size={12}/> Taşı</Btn>
        )}
        <div className="flex-1"/>
        <Btn size="sm" variant="secondary" onClick={onClose} data-testid="lot-loc-cancel">Vazgeç</Btn>
        <Btn size="sm" variant="primary" onClick={save} disabled={busy} data-testid="lot-loc-save"><Save size={12}/> Kaydet</Btn>
      </div>

      {moveOpen && (
        <div className="mt-4 pt-3 border-t border-slate-200 space-y-2" data-testid="lot-loc-move-panel">
          <div className="text-sm font-semibold text-slate-700">Lokasyon Taşı</div>
          <div className="grid grid-cols-3 gap-2">
            <Select label="Kaynak" value={move.from_code}
              onChange={(e) => setMove(m => ({ ...m, from_code: e.target.value }))}
              data-testid="lot-loc-move-from">
              <option value="">Seç...</option>
              {state.entries.map(e => <option key={e.location_code} value={e.location_code}>{e.location_code} ({e.qty})</option>)}
            </Select>
            <Input label="Hedef" value={move.to_code}
              onChange={(e) => setMove(m => ({ ...m, to_code: e.target.value.toUpperCase() }))}
              data-testid="lot-loc-move-to" placeholder="HMD-B-01-04"/>
            <Input label="Miktar" type="number" step="0.001" value={move.qty}
              onChange={(e) => setMove(m => ({ ...m, qty: Number(e.target.value) || 0 }))}
              data-testid="lot-loc-move-qty"/>
          </div>
          <div className="flex justify-end gap-2">
            <Btn size="sm" variant="secondary" onClick={() => setMoveOpen(false)}>İptal</Btn>
            <Btn size="sm" variant="primary" onClick={doMove} disabled={busy} data-testid="lot-loc-move-do">Taşı</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`p-2 border rounded ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-semibold font-mono mt-0.5">{value ?? 0}</div>
    </div>
  );
}

// Compact column cell for the Lots table. Shows single code, multi-location
// summary, or a dash if none. Clicking opens the dialog via `onClick`.
export function LocationCell({ lot, onClick }) {
  const entries = lot?.locations || [];
  if (!entries.length) {
    return (
      <button className="text-slate-400 hover:text-indigo-600 text-xs" onClick={onClick}
                data-testid={`lot-loc-cell-${lot.id}`} title="Lokasyon ekle">
        —
      </button>
    );
  }
  if (entries.length === 1) {
    return (
      <button className="font-mono text-xs text-indigo-700 hover:underline" onClick={onClick}
                data-testid={`lot-loc-cell-${lot.id}`}>
        {entries[0].location_code}
      </button>
    );
  }
  return (
    <button className="text-xs text-indigo-700 hover:underline" onClick={onClick}
              data-testid={`lot-loc-cell-${lot.id}`}
              title={entries.map(e => `${e.location_code} → ${e.qty}`).join("\n")}>
      {entries.length} Lokasyon
    </button>
  );
}
